// Design: languages, texts, look, consent and confirmation, allowed websites.

import React from 'react';
import { I } from '../../../common/v5/config';
import {
	applyPreset,
	BUILTIN_TEXTS,
	cssProblem,
	HEX,
	LANGUAGE_NAMES,
	parseAvatars,
	parseOrigins,
	PRESET_LIST,
	problemFor,
	setBuiltinText
} from './definition';
import { Check, Field, LangText, Section, Switch } from './ui';

// An optional colour: empty is the default; `keyword` (e.g. "transparent") is a one-click choice.
function ColourField({ label, hint, value, onChange, keyword, keywordLabel, error }) {
	const v = value || "";
	const bad = v !== "" && v !== keyword && !HEX.test(v);
	return (
		<Field label={label} hint={hint} error={bad ? I("Use a colour like #1f2328.") : error}>
			{id => (
				<div className="aif-colour">
					<input type="color" aria-label={I("Pick:") + " " + label} value={HEX.test(v) ? v : "#ffffff"}
						onChange={e => onChange(e.target.value)} />
					<input id={id} type="text" className="aif-mono" value={v} placeholder={I("Default")}
						onChange={e => onChange(e.target.value.trim() || null)} />
					{keyword && (
						<Check checked={v === keyword} label={keywordLabel}
							onChange={on => onChange(on ? keyword : null)} />
					)}
				</div>
			)}
		</Field>
	);
}

function Select({ label, value, onChange, options, error, hint }) {
	return (
		<Field label={label} error={error} hint={hint}>
			{id => (
				<select id={id} value={value} onChange={e => onChange(e.target.value)}>
					{options.map(o => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
				</select>
			)}
		</Field>
	);
}

export default function DesignTab({ defn, lang, problems, onChange }) {
	const main = defn.default_language;
	const theme = defn.theme;
	const setTheme = patch => onChange(Object.assign({}, defn, { theme: Object.assign({}, theme, patch) }));
	const set = patch => onChange(Object.assign({}, defn, patch));
	const p = path => problemFor(problems, path);

	const toggleLanguage = (code, on) => {
		const languages = on ? defn.languages.concat([code]) : defn.languages.filter(l => l !== code);
		if (languages.length === 0) {
			return;
		}
		set({ languages, default_language: languages.indexOf(main) >= 0 ? main : languages[0] });
	};

	const css = theme.custom_css || "";
	const cssError = cssProblem(css) || p("theme.custom_css");

	return (
		<div className="aif-stack">
			<Section title={I("Languages")} hint={I("The form follows the website's language when it is one of these; otherwise the main language.")}>
				<div className="aif-row" style={{ gap: "6px 16px" }}>
					{Object.keys(LANGUAGE_NAMES).map(code => (
						<Check key={code} checked={defn.languages.indexOf(code) >= 0} label={LANGUAGE_NAMES[code]}
							onChange={on => toggleLanguage(code, on)} />
					))}
				</div>
				{p("languages") && <div className="aif-error">{p("languages")}</div>}
				<div style={{ maxWidth: 220 }}>
					<Select label={I("Main language")} value={main} error={p("default_language")}
						onChange={default_language => set({ default_language })}
						options={defn.languages.map(code => [code, LANGUAGE_NAMES[code] || code])} />
				</div>
			</Section>

			<Section title={I("Heading")}>
				<LangText label={I("Title")} value={defn.title} lang={lang} mainLang={main} onChange={title => set({ title })} error={p("title")} />
				<LangText label={I("Subtitle")} value={defn.subtitle} lang={lang} mainLang={main} onChange={subtitle => set({ subtitle })} error={p("subtitle")} />
			</Section>

			<Section title={I("Start from a look")} hint={I("Sets colours, font and layout in one click. Your brand colour, logo, button text and own CSS stay.")}>
				<div className="aif-grid4">
					{PRESET_LIST.map(preset => (
						<button key={preset.key} type="button" className="aif-preset"
							onClick={() => set({ theme: applyPreset(theme, preset.key) })}>
							<b>{preset.label}</b>
							<span>{preset.hint}</span>
						</button>
					))}
				</div>
			</Section>

			<Section title={I("Colours")} hint={I("Leave a colour empty for the default.")}>
				<div className="aif-grid2">
					<Field label={I("Buttons and highlights")}
						error={HEX.test(theme.primary_color || "") ? p("theme.primary_color") : I("Use a colour like #6d28d9.")}>
						{id => (
							<div className="aif-colour">
								<input type="color" aria-label={I("Pick a colour")} value={HEX.test(theme.primary_color || "") ? theme.primary_color : "#6d28d9"}
									onChange={e => setTheme({ primary_color: e.target.value })} />
								<input id={id} type="text" className="aif-mono" value={theme.primary_color || ""}
									onChange={e => setTheme({ primary_color: e.target.value.trim() })} />
							</div>
						)}
					</Field>
					<ColourField label={I("Button text")} hint={I("Empty: black or white, whichever reads better.")}
						value={theme.button_text_color} onChange={button_text_color => setTheme({ button_text_color })} error={p("theme.button_text_color")} />
					<ColourField label={I("Text")} keyword="inherit" keywordLabel={I("Same as my website")}
						value={theme.text_color} onChange={text_color => setTheme({ text_color })} error={p("theme.text_color")} />
					<ColourField label={I("Form background")} keyword="transparent" keywordLabel={I("None (see-through)")}
						value={theme.background} onChange={background => setTheme({ background })} error={p("theme.background")} />
					<ColourField label={I("Field background")} value={theme.field_background}
						onChange={field_background => setTheme({ field_background })} error={p("theme.field_background")} />
					<ColourField label={I("Field and form border")} value={theme.field_border}
						onChange={field_border => setTheme({ field_border })} error={p("theme.field_border")} />
					<ColourField label={I("Page background (link only)")} hint={I("Around the form on its own page and in the iframe.")}
						value={theme.page_background} onChange={page_background => setTheme({ page_background })} error={p("theme.page_background")} />
				</div>
			</Section>

			<Section title={I("Font and size")}>
				<div className="aif-grid2">
					<Select label={I("Font")} value={theme.font} onChange={font => setTheme({ font })} error={p("theme.font")}
						options={[
							["inherit", I("Same as my website")],
							["system", I("The visitor's system font")],
							["inter", I("Inter (if your site loads it)")],
							["serif", I("Serif")],
							["custom", I("My own font…")],
						]} />
					<Select label={I("Text size")} value={theme.font_size || "normal"} onChange={font_size => setTheme({ font_size })}
						options={[["small", I("Small")], ["normal", I("Normal")], ["large", I("Large")]]} />
				</div>
				{theme.font === "custom" && (
					<Field label={I("Font name")} hint={I("As in your site's CSS, e.g. \"Montserrat\", sans-serif. Your site must load the font.")}
						error={p("theme.font_family") || p("theme")}>
						{id => <input id={id} type="text" className="aif-mono" value={theme.font_family || ""} placeholder={'"Montserrat", sans-serif'}
							onChange={e => setTheme({ font_family: e.target.value || null })} />}
					</Field>
				)}
				<p className="aif-muted aif-small">{I("\"Same as my website\" works when the form is in your page or popup. On the link and in the iframe it uses the system font.")}</p>
			</Section>

			<Section title={I("Layout")}>
				<div className="aif-grid3">
					<Select label={I("Style")} value={theme.style || "flat"} onChange={style => setTheme({ style })}
						options={[["card", I("Card with shadow")], ["flat", I("Box with a thin border")], ["none", I("No box (blends into the page)")]]} />
					<Select label={I("Spacing")} value={theme.spacing || "normal"} onChange={spacing => setTheme({ spacing })}
						options={[["compact", I("Compact")], ["normal", I("Normal")], ["roomy", I("Roomy")]]} />
					<Field label={I("Corners:") + " " + theme.radius + "px"} error={p("theme.radius")}>
						{id => <input id={id} type="range" min={0} max={16} value={theme.radius} style={{ width: "100%" }}
							onChange={e => setTheme({ radius: Number(e.target.value) })} />}
					</Field>
					<Field label={I("Maximum width")} hint={I("Empty: as wide as its space.")} error={p("theme.max_width")}>
						{id => (
							<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
								<input id={id} type="number" min={280} max={1200} step={10} value={theme.max_width == null ? "" : theme.max_width} placeholder="—"
									onChange={e => setTheme({ max_width: e.target.value ? Number(e.target.value) : null })} />
								<span className="aif-muted aif-small">px</span>
							</div>
						)}
					</Field>
					<Select label={I("Send button")} value={theme.button_align || "right"} onChange={button_align => setTheme({ button_align })}
						options={[["left", I("Left")], ["right", I("Right")], ["full", I("Full width")]]} />
					<LangText label={I("Send button text")} value={theme.button_label} lang={lang} mainLang={main}
						onChange={button_label => setTheme({ button_label })} hint={I("Empty: “Skicka” / “Send”.")} />
				</div>
				<Switch checked={!!theme.hide_header} onChange={hide_header => setTheme({ hide_header })}
					label={I("Hide the title and subtitle")} description={I("For pages that already have their own heading above the form.")} />
			</Section>

			<Section title={I("Logo and team")}>
				<Field label={I("Logo URL (optional)")} hint={I("An https:// image address.")} error={p("theme.logo_url")}>
					{id => <input id={id} type="text" value={theme.logo_url || ""} placeholder="https://"
						onChange={e => setTheme({ logo_url: e.target.value.trim() || null })} />}
				</Field>
				<Field label={I("Team photos (optional, up to 3)")} hint={I("One https:// image address per line, shown as small round avatars.")} error={p("theme.avatars")}>
					{id => <textarea id={id} rows={2} key={theme.avatars.join("\n")} defaultValue={theme.avatars.join("\n")}
						onBlur={e => setTheme({ avatars: parseAvatars(e.target.value) })} />}
				</Field>
			</Section>

			<Section title={I("Your own CSS (advanced)")}
				hint={<span>{I("Applies only inside the form, never to the rest of your site. Useful classes:")} <code>.cf</code> {I("(the form)")}, <code>h2</code>, <code>.sub</code>, <code>.lb</code> {I("(labels)")}, <code>input</code>, <code>textarea</code>, <code>.pri</code> {I("(send button)")}, <code>.ans</code> {I("(AI answer)")}.</span>}>
				<textarea aria-label={I("Your own CSS")} rows={5} className="aif-mono" value={css}
					placeholder=".pri { border-radius: 999px; text-transform: uppercase; }"
					onChange={e => setTheme({ custom_css: e.target.value })} />
				{cssError && <div className="aif-error" role="alert">{cssError}</div>}
			</Section>

			<Section title={I("Built-in texts") + (lang !== main ? " (" + lang.toUpperCase() + ")" : "")}
				hint={I("The form's own words. Leave a text empty to keep the default shown in grey. Labels and the send button are set above and on the Fields tab.")}>
				<details>
					<summary>{I("Show the {n} texts").replace("{n}", BUILTIN_TEXTS.length)}</summary>
					<div className="aif-grid2" style={{ marginTop: 12 }}>
						{BUILTIN_TEXTS.map(t => (
							<Field key={t.key} label={t.label} error={p("texts." + t.key)}>
								{id => (
									<input id={id} type="text"
										value={(defn.texts && defn.texts[t.key] && defn.texts[t.key][lang]) || ""}
										placeholder={lang === "sv" ? t.sv : t.en}
										onChange={e => onChange(setBuiltinText(defn, t.key, lang, e.target.value))} />
								)}
							</Field>
						))}
					</div>
				</details>
				{p("texts") && <div className="aif-error">{p("texts")}</div>}
			</Section>

			<Section title={I("Consent and privacy")}>
				<LangText label={I("Consent text")} multiline value={defn.consent_text} lang={lang} mainLang={main}
					onChange={consent_text => set({ consent_text })} error={p("consent_text")}
					hint={I("Shown above the buttons, e.g. how you use the customer's details.")} />
				<Field label={I("Privacy policy link")} error={p("privacy_url")}>
					{id => <input id={id} type="text" value={defn.privacy_url || ""} placeholder="https://"
						onChange={e => set({ privacy_url: e.target.value.trim() || null })} />}
				</Field>
			</Section>

			<Section title={I("After sending")}>
				<LangText label={I("Title")} value={defn.confirmation.title} lang={lang} mainLang={main}
					onChange={title => set({ confirmation: Object.assign({}, defn.confirmation, { title }) })} error={p("confirmation.title")} />
				<LangText label={I("Text")} multiline value={defn.confirmation.text} lang={lang} mainLang={main}
					onChange={text => set({ confirmation: Object.assign({}, defn.confirmation, { text }) })}
					hint={I("{reference} is replaced with the errand reference.")} error={p("confirmation.text")} />
			</Section>

			<Section title={I("Allowed websites (optional)")} hint={I("Leave empty to allow the form on any website. Otherwise one address per line, e.g. https://www.yourshop.se.")}>
				<textarea aria-label={I("Allowed websites")} rows={3} className="aif-mono"
					key={defn.allowed_origins.join("\n")}
					defaultValue={defn.allowed_origins.join("\n")}
					onBlur={e => set({ allowed_origins: parseOrigins(e.target.value) })} />
				{p("allowed_origins") && <div className="aif-error">{p("allowed_origins")}</div>}
			</Section>
		</div>
	);
}
